@file:Suppress("MagicNumber")

package com.nonopn.skydome.common.app.pages.pdf

import com.nonopn.skydome.common.app.utils.fmt

import kotlin.math.PI
import kotlin.math.atan
import kotlin.math.cos
import kotlin.math.floor
import kotlin.math.ln
import kotlin.math.sinh
import kotlin.math.tan

/**
 * Satellite tile utilities for SVG map backgrounds in PDFs (ESRI World Imagery).
 */
object MapTileUtils {

    private const val KM_PER_DEG = 111.32
    private const val EARTH_CIRC = 40075.0

    data class TileSpec(
        val z: Int, val x: Int, val y: Int,
        val destX: Double, val destY: Double,
        val destW: Double, val destH: Double,
    )

    fun tileUrl(z: Int, x: Int, y: Int): String =
        "https://server.arcgisonline.com/ArcGIS/rest/services/" +
            "World_Imagery/MapServer/tile/$z/$y/$x"

    private fun toFrac(lat: Double, lon: Double, z: Int): Pair<Double, Double> {
        val n = (1 shl z).toDouble()
        val tx = (lon + 180.0) / 360.0 * n
        val r = lat * PI / 180.0
        val ty = (1.0 - ln(tan(r) + 1.0 / cos(r)) / PI) / 2.0 * n
        return tx to ty
    }

    private fun tileNW(tx: Int, ty: Int, z: Int): Pair<Double, Double> {
        val n = (1 shl z).toDouble()
        val lon = tx / n * 360.0 - 180.0
        val lat = atan(sinh(PI * (1.0 - 2.0 * ty / n))) * 180.0 / PI
        return lat to lon
    }

    private fun chooseZoom(diamKm: Double, lat: Double): Int {
        val c = cos(lat * PI / 180.0)
        for (z in 15 downTo 1) {
            val tileKm = EARTH_CIRC * c / (1 shl z)
            if (tileKm >= diamKm * 0.4) return z
        }
        return 1
    }

    /** Compute tile layout for a given center, view radius (km) and pixel size. */
    fun tileSpecs(cLat: Double, cLon: Double, viewKm: Double, size: Int): List<TileSpec> {
        val cx = size / 2.0
        val sc = cx / viewKm
        val cosL = cos(cLat * PI / 180.0)
        val z = chooseZoom(viewKm * 2, cLat)
        val latSpan = viewKm / KM_PER_DEG
        val lonSpan = viewKm / (KM_PER_DEG * cosL)
        val (txMinF, _) = toFrac(cLat, cLon - lonSpan, z)
        val (txMaxF, _) = toFrac(cLat, cLon + lonSpan, z)
        val (_, tyMinF) = toFrac(cLat + latSpan, cLon, z)
        val (_, tyMaxF) = toFrac(cLat - latSpan, cLon, z)
        val out = mutableListOf<TileSpec>()
        for (ty in floor(tyMinF).toInt()..floor(tyMaxF).toInt()) {
            for (tx in floor(txMinF).toInt()..floor(txMaxF).toInt()) {
                val (nLat, nLon) = tileNW(tx, ty, z)
                val (sLat, sLon) = tileNW(tx + 1, ty + 1, z)
                val x1 = cx + (nLon - cLon) * KM_PER_DEG * cosL * sc
                val y1 = cx - (nLat - cLat) * KM_PER_DEG * sc
                val x2 = cx + (sLon - cLon) * KM_PER_DEG * cosL * sc
                val y2 = cx - (sLat - cLat) * KM_PER_DEG * sc
                out.add(TileSpec(z, tx, ty, x1, y1, x2 - x1, y2 - y1))
            }
        }
        return out
    }

    /** SVG &lt;image&gt; tags for satellite tile background. */
    fun svgTileImages(cLat: Double, cLon: Double, viewKm: Double, size: Int): String =
        buildString {
            for (t in tileSpecs(cLat, cLon, viewKm, size)) {
                val url = tileUrl(t.z, t.x, t.y)
                append("<image href=\"$url\" x=\"${t.destX.fmt(1)}\" ")
                append("y=\"${t.destY.fmt(1)}\" width=\"${t.destW.fmt(1)}\" ")
                append("height=\"${t.destH.fmt(1)}\" preserveAspectRatio=\"none\"/>")
            }
        }
}
